import { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import { getTeamLeaderboard, type LeaderboardEntry } from '@tss/shared';
import { getInitials } from '@tss/shared/utils';

interface TeamLeaderboardProps {
  orgId: string;
  teamId: string;
  teamName?: string | null;
  /** Highlights this row in the list (the logged-in athlete). */
  currentUserId?: string | null;
  /** Cap how many entries we show. The athlete's own row is always included
   *  even if it falls outside the cap. */
  limit?: number;
}

const RANK_BADGE: Record<number, string> = {
  1: '🥇',
  2: '🥈',
  3: '🥉',
};

export function TeamLeaderboard({
  orgId,
  teamId,
  teamName,
  currentUserId,
  limit = 5,
}: TeamLeaderboardProps) {
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setEntries(null);
    getTeamLeaderboard(orgId, teamId)
      .then((res) => {
        if (!cancelled) setEntries(res.leaderboard);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, teamId]);

  if (error) {
    return null; // Don't break the page over a leaderboard hiccup
  }

  const visible = (() => {
    if (!entries) return null;
    const top = entries.slice(0, limit);
    if (currentUserId && !top.some((e) => e.participant_id === currentUserId)) {
      const own = entries.find((e) => e.participant_id === currentUserId);
      if (own) return [...top, own];
    }
    return top;
  })();

  return (
    <section
      className="card"
      style={{
        marginBottom: '20px',
        padding: '16px 18px',
      }}
      aria-label="Team leaderboard"
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}
      >
        <Trophy size={18} style={{ color: '#f59e0b' }} />
        <h2
          style={{
            fontSize: '15px',
            fontWeight: 600,
            color: 'var(--gray-900)',
            margin: 0,
          }}
        >
          Leaderboard{teamName ? ` — ${teamName}` : ''}
        </h2>
      </header>

      {visible === null && <LeaderboardSkeleton rows={limit} />}

      {visible !== null && visible.length === 0 && (
        <p
          style={{
            color: 'var(--gray-500)',
            fontSize: '13px',
            margin: 0,
            padding: '12px 0',
          }}
        >
          No assessments yet. Once your coach grades the team, you'll see rankings here.
        </p>
      )}

      {visible !== null && visible.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          {visible.map((entry) => (
            <LeaderboardRow
              key={entry.participant_id}
              entry={entry}
              isSelf={entry.participant_id === currentUserId}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function LeaderboardRow({ entry, isSelf }: { entry: LeaderboardEntry; isSelf: boolean }) {
  const badge = entry.rank != null ? RANK_BADGE[entry.rank] : null;
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 10px',
        borderRadius: 'var(--radius-md)',
        background: isSelf ? 'rgba(var(--purple-rgb), 0.08)' : 'var(--gray-50)',
        border: isSelf ? '1px solid rgba(var(--purple-rgb), 0.25)' : '1px solid transparent',
      }}
    >
      <div
        style={{
          width: '28px',
          textAlign: 'center',
          fontSize: badge ? '18px' : '13px',
          fontWeight: 600,
          color: 'var(--gray-700)',
        }}
      >
        {badge || (entry.rank != null ? `#${entry.rank}` : '—')}
      </div>

      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '7px',
          overflow: 'hidden',
          flexShrink: 0,
          background: 'linear-gradient(135deg, var(--purple-600) 0%, var(--purple-500) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        {entry.profile_picture_url ? (
          <img
            src={entry.profile_picture_url}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 20%',
            }}
          />
        ) : (
          <span>
            {getInitials({
              given_name: entry.participant_name.split(' ')[0],
              family_name: entry.participant_name.split(' ').slice(1).join(' '),
            })}
          </span>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--gray-900)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {entry.participant_name}
          {isSelf && (
            <span style={{ color: 'var(--purple-600)', fontSize: '12px', marginLeft: 6 }}>
              (you)
            </span>
          )}
        </p>
        {entry.position && (
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--gray-500)' }}>{entry.position}</p>
        )}
      </div>

      <div
        style={{
          fontSize: '15px',
          fontWeight: 700,
          color: entry.current_value != null ? 'var(--gray-900)' : 'var(--gray-400)',
          minWidth: '24px',
          textAlign: 'right',
        }}
      >
        {entry.current_value != null ? entry.current_value : '–'}
      </div>
    </li>
  );
}

function LeaderboardSkeleton({ rows }: { rows: number }) {
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <li
          key={i}
          style={{
            height: '44px',
            borderRadius: 'var(--radius-md)',
            background: 'linear-gradient(90deg, var(--gray-100), var(--gray-200), var(--gray-100))',
            backgroundSize: '200% 100%',
            animation: 'tss-skeleton-shimmer 1.4s ease-in-out infinite',
          }}
        />
      ))}
    </ul>
  );
}
